import { useState, useEffect, useCallback } from 'react';
import { userSession } from '../utils/userSession.js';
import { PencilIcon, UserIcon, EnvelopeIcon, GlobeAltIcon, PhotoIcon, BanknotesIcon, CalendarIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useData } from '../context/DataContext.js';
import AddPartner from './AddPartner.js';
import EditPartner from './EditPartner.js';
import CreateSubscription from './CreateSubscription.js';
import CancelSubscription from './CancelSubscription.js';
import { format } from 'date-fns';

export default function UserDashboardPartner() {
  // Core data
  const { partners, refreshPartners } = useData();
  const isPartner = userSession.hasUserType(3);
  const partnerTypeDetails = userSession.getUser()?.userTypeDetails?.filter(d => d.typeId === 3) || [];

  // State
  const [selectedPartnershipId, setSelectedPartnershipId] = useState(null);
  const [stats, setStats] = useState({
    bannerClicks: 0,
    uniqueClicks: 0,
    optInUsers: 0,
    subscriptionStatus: 'not_setup',
    isPaid: 0,
    current_period_end: null
  });

  // UI state
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [showEditPartner, setShowEditPartner] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);

  // Derived data
  const partnerships = partnerTypeDetails.map((detail, index) => {
    const partner = partners?.find(p => Number(p.PartnerID) === Number(detail.linkId));
    return {
      ...detail,
      partnerName: partner?.PartnerName || 'Loading...',
      isPaid: partner?.isPaid || 0,
      partnerData: partner,
      index // Add index to track order
    };
  });

  // Update selectedPartnership to use index for initial selection
  const selectedPartnership = partnerships.find(p => p.linkId === selectedPartnershipId) 
    || partnerships[0];

  // Update the initial selection effect
  useEffect(() => {
    if (!selectedPartnershipId && partnerships.length > 0) {
      // Set the ID of the first partnership
      setSelectedPartnershipId(partnerships[0].linkId);
    }
  }, [partnerships, selectedPartnershipId]);

  // Stats fetching
  const fetchStats = useCallback(async (partnershipId) => {
    if (!partnershipId) return;

    try {
      const response = await fetch(
        `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/partnerstats/${partnershipId}`
      );
      const statsData = await response.json();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching partner stats:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership?.linkId) {
      fetchStats(selectedPartnership.linkId);
    }
  }, [selectedPartnership?.linkId, fetchStats]);

  // Handlers
  const handlePartnerCreated = async () => {
    await refreshPartners();
    setShowAddPartner(false);
  };

  const handlePartnerUpdated = async () => {
    try {
      // Refresh partners data to get updated details
      await refreshPartners();
      
      // Refresh stats for the current partnership
      if (selectedPartnership?.linkId) {
        await fetchStats(selectedPartnership.linkId);
      }
      
      setShowEditPartner(false);
    } catch (error) {
      console.error('Error refreshing data after update:', error);
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelDialog(true);
  };

  const handleCancellationSuccess = async () => {
    try {
      // Refresh partners data to get updated subscription status
      await refreshPartners();
      
      // Refresh stats for the current partnership
      if (selectedPartnership?.linkId) {
        await fetchStats(selectedPartnership.linkId);
      }
      
      setShowCancelDialog(false);
    } catch (error) {
      console.error('Error refreshing data after cancellation:', error);
    }
  };

    // Update the subscription status display
    const getSubscriptionDisplay = (subscriptionStatus, isPaid) => {
      if (subscriptionStatus === 'canceling') return 'Cancelling';
      if (isPaid === 1) return 'Active';
      return 'Inactive';
    };
  
  // Add a function to count active partnerships
  const getActivePartnershipsCount = () => {
    return partnerships.filter(p => p.partnerData?.isPaid === 1).length;
  };

  // If not a partner, show registration option
  if (!isPartner) {
    return (
      <div className="p-1">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            You are not currently a partner
          </h2>
          <p className="text-sm text-gray-600">
            Register below to become a partner
          </p>
        </div>

        <div className="space-y-6">
          {/* Register as new partner */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Register as a new partner
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a new partner profile
            </p>
            <button
              className="w-full bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800 transition-colors"
              onClick={() => setShowAddPartner(true)}
            >
              Register New Partner
            </button>
          </div>
        </div>

        {/* Make sure the AddPartner modal is included here */}
        <AddPartner 
          isOpen={showAddPartner}
          onClose={() => setShowAddPartner(false)}
          onPartnerCreated={handlePartnerCreated}
        />
      </div>
    );
  }

  // Main partner dashboard
  return (
    <div className="p-1">
      {/* Stats Grid */}
      {selectedPartnership && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          {/* Banner Clicks */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg p-4 text-center h-[100px] flex flex-col justify-center">
            <div className="text-4xl font-bold text-white">
              {stats?.bannerClicks || 0}
            </div>
            <div className="text-xs text-white">
              clicks from banner
            </div>
          </div>

          {/* Partner List Clicks */}
          <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 text-center h-[100px] flex flex-col justify-center">
            <div className="text-4xl font-bold text-white">
              {stats?.uniqueClicks || 0}
            </div>
            <div className="text-xs text-white">
              clicks from partner list
            </div>
          </div>

          {/* Opt-in Users */}
          <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 text-center h-[100px] flex flex-col justify-center">
            <div className="text-4xl font-bold text-white">
              {stats?.optInUsers || 0}
            </div>
            <div className="text-xs text-white">
              users with opt in
            </div>
          </div>

          {/* Subscription Status */}
          <div className="flex flex-col">
            <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 text-center h-[100px] flex flex-col justify-center">
              <div className="text-4xl font-bold text-white leading-tight">
                <span className="block text-2xl sm:text-2xl md:text-3xl lg:text-4xl">
                  {getActivePartnershipsCount()}
                </span>
              </div>
              <div className="text-xs text-white">
                active partnerships
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Partnerships Section */}
      <div>
        <div className="flex items-center justify-between mb-2 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">Manage Partnerships</h2>
          <button 
            className="flex items-center px-4 py-1 text-xs text-green-700 rounded-md hover:text-green-600"
            onClick={() => setShowAddPartner(true)}
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Add
          </button>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent mb-2"></div>
      </div>

      <div>
        {selectedPartnership && (
          <div className="sticky top-[81px] z-[5] -mx-1 mb-2 bg-gray-50 px-1 pb-2">
            <button
              type="button"
              onClick={() => setSelectedPartnershipId(selectedPartnership.linkId)}
              className="w-full text-left p-2 rounded-lg border border-slate-600 bg-slate-200 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{selectedPartnership.partnerName}</p>
                  <p className="text-sm text-gray-500">
                    {selectedPartnership.isPaid ? '(Active)' : '(Inactive)'}
                  </p>
                </div>
                <span className="text-xs text-slate-600">Selected</span>
              </div>
            </button>
          </div>
        )}

        {partnerships.filter((partnership) => partnership.linkId !== selectedPartnership?.linkId).length > 0 && (
          <div className="mb-2">
            {partnerships
              .filter((partnership) => partnership.linkId !== selectedPartnership?.linkId)
              .map((partnership) => (
                <button
                  key={partnership.linkId}
                  type="button"
                  onClick={() => setSelectedPartnershipId(partnership.linkId)}
                  className="w-full text-left p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{partnership.partnerName}</p>
                      <p className="text-sm text-gray-500">
                        {partnership.isPaid ? '(Active)' : '(Inactive)'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
          </div>
        )}

      {/* Selected Partnership Details */}
      {selectedPartnership && (
        <div className="bg-white border rounded-lg p-4 shadow-lg mb-2">
          <div className="space-y-2">
            {/* 1. Partner Name with Logo */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">
                {selectedPartnership?.partnerData?.PartnerName || ''}
              </h1>
              {selectedPartnership?.partnerData?.PartnerLogo && (
                <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-2 flex items-center justify-center">
                  <img 
                    src={selectedPartnership?.partnerData?.PartnerLogo} 
                    alt={`${selectedPartnership?.partnerData?.PartnerName} logo`}
                    className="h-8 w-auto object-contain"
                  />
                </div>
              )}
            </div>

            {/* 2. Subscription Details Section */}
            <div className="border-t pt-2">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Subscription Details</h3>
              <div className="space-y-1">
                {/* Status */}
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <BanknotesIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <span>
                    Status: {getSubscriptionDisplay(
                      selectedPartnership?.partnerData?.subscription_status,
                      selectedPartnership?.partnerData?.isPaid
                    )}
                  </span>
                </div>

                {/* Period End */}
                {selectedPartnership?.partnerData?.current_period_end && (
                  <div className="flex items-center text-sm text-slate-600">
                    <span className="w-8 flex justify-center">
                      <CalendarIcon className="h-5 w-5 text-slate-400" />
                    </span>
                    <span>
                      {selectedPartnership?.partnerData?.subscription_status === 'canceling' 
                        ? `Ending: ${format(new Date(selectedPartnership.partnerData.current_period_end), 'dd MMM yyyy')}`
                        : `Next billing: ${format(new Date(selectedPartnership.partnerData.current_period_end), 'dd MMM yyyy')}`
                      }
                    </span>
                  </div>
                )}

                {/* Action Button */}
                <div className="flex items-center text-sm pt-1">
                  <span className="w-8" /> {/* Spacer to align with icons above */}
                  {selectedPartnership?.partnerData?.subscription_status === 'canceling' ? (
                    <span className="text-orange-600">You will not be billed again</span>
                  ) : selectedPartnership?.partnerData?.isPaid === 1 ? (
                    <button
                      onClick={handleCancelSubscription}
                      className="text-red-600 hover:text-red-500"
                    >
                      Cancel Subscription
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowSubscription(true)}
                      className="text-green-900 hover:text-green-700"
                    >
                      Create Subscription
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 3. Contact Details */}
            <div className="border-t pt-2">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Contact Details</h3>
              <div className="space-y-1">
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <UserIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <span>{selectedPartnership?.partnerData?.PartnerContact}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <a 
                    href={`mailto:${selectedPartnership?.partnerData?.PartnerEmail}`}
                    className="hover:text-slate-900"
                  >
                    {selectedPartnership?.partnerData?.PartnerEmail}
                  </a>
                </div>
                {selectedPartnership?.partnerData?.PartnerWebsiteLink && (
                  <div className="flex items-center text-sm text-slate-600">
                    <span className="w-8 flex justify-center">
                      <GlobeAltIcon className="h-5 w-5 text-slate-400" />
                    </span>
                    <a 
                      href={selectedPartnership?.partnerData?.PartnerWebsiteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-slate-900"
                    >
                      {selectedPartnership?.partnerData?.PartnerWebsiteLink}
                    </a>
                  </div>
                )}
                {selectedPartnership?.partnerData?.PartnerLogo && (
                  <div className="flex items-center text-sm text-slate-600">
                    <span className="w-8 flex justify-center">
                      <PhotoIcon className="h-5 w-5 text-slate-400" />
                    </span>
                    <a 
                      href={selectedPartnership?.partnerData?.PartnerLogo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-slate-900 truncate max-w-[400px]"
                      title={selectedPartnership?.partnerData?.PartnerLogo}
                    >
                      {selectedPartnership?.partnerData?.PartnerLogo}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* 4. Banner Section */}
            <div className="border-t pt-2">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Banner</h3>
              <div className="space-y-1">
                {/* Banner Web Link */}
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <GlobeAltIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <a 
                    href={selectedPartnership?.partnerData?.PartnerObjLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-slate-900 truncate max-w-[400px]"
                    title={selectedPartnership?.partnerData?.PartnerObjLink}
                  >
                    {selectedPartnership?.partnerData?.PartnerObjLink}
                  </a>
                </div>

                {/* Banner Image Link */}
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <PhotoIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <span className="truncate max-w-[400px]" title={selectedPartnership?.partnerData?.PartnerObjImage}>
                    {selectedPartnership?.partnerData?.PartnerObjImage}
                  </span>
                </div>

                {/* Banner Preview */}
                {selectedPartnership?.partnerData?.PartnerObjImage && (
                  <div className="flex justify-center mt-4">
                    <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 flex items-center justify-center">
                      <img 
                        src={selectedPartnership?.partnerData?.PartnerObjImage}
                        alt="Banner preview"
                        className="h-8 w-auto object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Edit Button */}
            <div className="flex items-center justify-end border-t pt-6">
              <button
                className="flex items-center text-xs text-slate-600 hover:text-slate-800"
                onClick={() => setShowEditPartner(true)}
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Modals */}
      <AddPartner 
        isOpen={showAddPartner}
        onClose={() => setShowAddPartner(false)}
        onPartnerCreated={handlePartnerCreated}
      />
      <EditPartner 
        isOpen={showEditPartner}
        onClose={() => setShowEditPartner(false)}
        onPartnerUpdated={handlePartnerUpdated}
        partnerData={selectedPartnership?.partnerData}
      />
      <CreateSubscription 
        isOpen={showSubscription}
        onClose={() => setShowSubscription(false)}
        partnerId={selectedPartnership?.linkId}
      />
      <CancelSubscription
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        partnerId={selectedPartnership?.linkId}
        onSuccess={handleCancellationSuccess}
      />
    </div>
  );
} 